import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button, Badge, Form } from 'react-bootstrap';
import { PersonFill, ShieldCheck, Eye, ChatSquare, FileText, Download } from 'react-bootstrap-icons';
import { Auth } from 'aws-amplify';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stats, setStats] = useState({});
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [currentUser, setCurrentUser] = useState(null);
  const [updatingRoles, setUpdatingRoles] = useState(new Set());

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${config.api.url}admin/users?format=json`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data.users || []);
      
      // Calculate stats
      const totalUsers = data.users?.length || 0;
      const confirmedUsers = data.users?.filter(u => u.status === 'CONFIRMED').length || 0;
      const adminUsers = data.users?.filter(u => u.is_admin).length || 0;
      const reviewerUsers = data.users?.filter(u => u.is_reviewer).length || 0;
      
      setStats({
        total: totalUsers,
        confirmed: confirmedUsers,
        unconfirmed: totalUsers - confirmedUsers,
        admins: adminUsers,
        reviewers: reviewerUsers
      });
      
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUser = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      setCurrentUser(user.attributes.sub);
    } catch (err) {
      console.error('Error getting current user:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    getCurrentUser();
  }, []);

  const getRoleBadges = (user) => {
    const badges = [];
    if (user.is_admin) {
      badges.push(
        <Badge key="admin" bg="danger" className="me-1">
          <ShieldCheck className="me-1" size={12} />
          Admin
        </Badge>
      );
    }
    if (user.is_reviewer) {
      badges.push(
        <Badge key="reviewer" bg="info" className="me-1">
          <Eye className="me-1" size={12} />
          Reviewer
        </Badge>
      );
    }
    if (badges.length === 0) {
      badges.push(
        <Badge key="user" bg="secondary" className="me-1">
          <PersonFill className="me-1" size={12} />
          User
        </Badge>
      );
    }
    return badges;
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.user_id)));
    }
  };

  const updateUserRole = async (userIds, roleType, newValue) => {
    // Check if trying to remove admin from self
    if (userIds.includes(currentUser) && roleType === 'admin' && !newValue) {
      setError('You cannot remove admin role from yourself');
      return;
    }

    const updateKey = `bulk-${roleType}`;
    setUpdatingRoles(prev => new Set(prev).add(updateKey));
    
    try {
      const promises = userIds.map(async (userId) => {
        const user = users.find(u => u.user_id === userId);
        const updatedRoles = {
          isAdmin: roleType === 'admin' ? newValue : user.is_admin,
          isReviewer: roleType === 'reviewer' ? newValue : user.is_reviewer,
          currentUserId: currentUser
        };

        const response = await fetch(`${config.api.url}admin/users/${userId}/roles`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedRoles),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to update role for user ${userId}`);
        }

        return response.json();
      });

      await Promise.all(promises);

      // Refresh users list
      await fetchUsers();
      
      // Clear selection after successful bulk operation
      setSelectedUsers(new Set());
      
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingRoles(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  };

  const downloadCSV = () => {
    // Prepare CSV data
    const csvData = [
      // Header row
      ['Name', 'Email', 'Email Verified', 'Status', 'Admin', 'Reviewer', 'Questions', 'Submissions', 'User ID']
    ];

    // Add user data rows
    users.forEach(user => {
      csvData.push([
        user.full_name || 'N/A',
        user.email || 'N/A',
        user.email_verified ? 'Yes' : 'No',
        user.status || 'Unknown',
        user.is_admin ? 'Yes' : 'No',
        user.is_reviewer ? 'Yes' : 'No',
        user.question_count || 0,
        user.submission_count || 0,
        user.user_id || 'N/A'
      ]);
    });

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const StatCard = ({ title, value, subtitle, color = "primary" }) => (
    <Col md={6} lg={2} className="mb-3">
      <Card className="text-center h-100">
        <Card.Body>
          <h3 className={`text-${color} mb-1`}>{value}</h3>
          <Card.Title className="h6 mb-0">{title}</Card.Title>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </Card.Body>
      </Card>
    </Col>
  );

  if (loading) {
    return <LoadingSpinner message="Loading user data..." />;
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Users</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchUsers}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">
            <PersonFill className="me-2" />
            User Management
          </h1>
          <p className="text-muted mb-0">
            Manage user roles and permissions
          </p>
        </div>
        <Button variant="outline-primary" onClick={fetchUsers} disabled={loading}>
          🔄 Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <StatCard
          title="Total Users"
          value={stats.total}
          color="primary"
        />
        <StatCard
          title="Confirmed"
          value={stats.confirmed}
          color="success"
        />
        <StatCard
          title="Unconfirmed"
          value={stats.unconfirmed}
          color="warning"
        />
        <StatCard
          title="Admins"
          value={stats.admins}
          color="danger"
        />
        <StatCard
          title="Reviewers"
          value={stats.reviewers}
          color="info"
        />
      </Row>

      {/* Bulk Actions */}
      {selectedUsers.size > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <span>
                <strong>{selectedUsers.size}</strong> user{selectedUsers.size !== 1 ? 's' : ''} selected
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={updatingRoles.has('bulk-admin')}
                  onClick={() => updateUserRole(Array.from(selectedUsers), 'admin', true)}
                >
                  {updatingRoles.has('bulk-admin') ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <ShieldCheck className="me-1" size={14} />
                  )}
                  Make Admin
                </Button>
                
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={updatingRoles.has('bulk-admin')}
                  onClick={() => updateUserRole(Array.from(selectedUsers), 'admin', false)}
                >
                  {updatingRoles.has('bulk-admin') ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <ShieldCheck className="me-1" size={14} />
                  )}
                  Remove Admin
                </Button>
                
                <Button
                  variant="info"
                  size="sm"
                  disabled={updatingRoles.has('bulk-reviewer')}
                  onClick={() => updateUserRole(Array.from(selectedUsers), 'reviewer', true)}
                >
                  {updatingRoles.has('bulk-reviewer') ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <Eye className="me-1" size={14} />
                  )}
                  Make Reviewer
                </Button>
                
                <Button
                  variant="outline-info"
                  size="sm"
                  disabled={updatingRoles.has('bulk-reviewer')}
                  onClick={() => updateUserRole(Array.from(selectedUsers), 'reviewer', false)}
                >
                  {updatingRoles.has('bulk-reviewer') ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <Eye className="me-1" size={14} />
                  )}
                  Remove Reviewer
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => setSelectedUsers(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">User List ({users.length} users)</h5>
            {lastUpdated && (
              <small className="text-muted">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </small>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {users.length === 0 ? (
            <div className="text-center py-4">
              <PersonFill size={48} className="text-muted mb-3" />
              <p className="text-muted">No users found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{width: '40px'}}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.size === users.length && users.length > 0}
                        onChange={handleSelectAll}
                        indeterminate={selectedUsers.size > 0 && selectedUsers.size < users.length}
                      />
                    </th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th className="text-center" style={{width: '80px'}}>
                      <ChatSquare size={16} title="Questions" />
                    </th>
                    <th className="text-center" style={{width: '80px'}}>
                      <FileText size={16} title="Submissions" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.user_id || index}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsers.has(user.user_id)}
                          onChange={() => handleSelectUser(user.user_id)}
                        />
                      </td>
                      <td>
                        <strong>{user.full_name || 'N/A'}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">
                            {user.email || 'N/A'}
                          </span>
                          {user.email_verified ? (
                            <span className="text-success" title="Email verified">✅</span>
                          ) : (
                            <span className="text-warning" title="Email not verified">❌</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap">
                          {getRoleBadges(user)}
                        </div>
                      </td>
                      <td className="text-center">
                        <Badge bg="info" pill>
                          {user.question_count || 0}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="primary" pill>
                          {user.submission_count || 0}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Download CSV Button */}
      <div className="d-flex justify-content-end mt-3">
        <Button 
          variant="outline-success" 
          onClick={downloadCSV}
          disabled={users.length === 0}
        >
          <Download className="me-2" size={16} />
          Download CSV
        </Button>
      </div>
    </Container>
  );
};

export default AdminUsersPage;
